import type { FaqItem } from "./structuredData";

// Single source of truth for FAQ content rendered on /faq, including the
// combined FAQPage JSON-LD graph. Questions are grouped by customer concern
// and kept industry-neutral where possible so the flat list stays unique.

export interface FaqGroup {
  id: string;
  label: string;
  faqs: FaqItem[];
}

export const faqGroups: FaqGroup[] = [
  {
    id: "data-ownership",
    label: "Data Ownership & Privacy",
    faqs: [
      {
        question: "Where does our data live?",
        answer:
          "Your data stays in the environment your organization approves and controls. We design each solution around your existing security, governance, and retention requirements, and we do not keep copies of your documents, indexes, or operational data after the engagement requires them.",
      },
      {
        question: "Can Contextral see our data?",
        answer:
          "Not unless you explicitly grant temporary, purpose-limited access for deployment, testing, or support. Access is scoped to the work being performed, follows your approval process, and can be revoked by your team at any time.",
      },
      {
        question: "Do you keep remote access to our systems?",
        answer:
          "No persistent access is required. If ongoing support needs remote access, it is enabled only through a method your organization approves, limited by role and duration, and removed when the support task is complete.",
      },
    ],
  },
  {
    id: "security-compliance",
    label: "Security & Compliance",
    faqs: [
      {
        question: "Is Contextral suitable for regulated or privileged data?",
        answer:
          "Yes. We build secure workflows for environments governed by requirements such as HIPAA, public-sector records policies, and legal privilege. Each engagement starts with your applicable controls so the solution fits your compliance obligations rather than forcing a generic operating model.",
      },
      {
        question: "How are Contextral solutions secured?",
        answer:
          "Security is designed into the solution through encryption, role-based access controls, network segmentation, identity integration, least-privilege permissions, and documented data flows. The exact controls are tailored to your risk profile and existing security standards.",
      },
      {
        question: "Can your automation help us stay audit-ready?",
        answer:
          "Yes. We can structure records, preserve source references, standardize approvals, and create traceable workflow histories so teams can find supporting documentation quickly and demonstrate how information moved through a process.",
      },
    ],
  },
  {
    id: "how-we-work",
    label: "How We Work",
    faqs: [
      {
        question: "What does a Contextral project look like?",
        answer:
          "Our customer success process moves through six stages: Discovery, Assessment, Tailored Design, Solution Engineering, Private Deployment, and Continued Support. That sequence keeps the work grounded in a real operational problem and gives your team clear review points before anything changes in production.",
      },
      {
        question: "How long does an automation project take?",
        answer:
          "Timing depends on the workflow, systems involved, data readiness, and approval requirements. We deploy in practical phases so your organization can validate value early while day-to-day operations keep running.",
      },
      {
        question: "What do we need to provide?",
        answer:
          "We typically need a business and technical point of contact, approved access to the systems or data involved, and input from the people who perform the workflow today. We handle discovery, solution design, integration, testing, training, and rollout with your team.",
      },
    ],
  },
  {
    id: "deployment",
    label: "Deployment & Environment",
    faqs: [
      {
        question: "Where can a Contextral solution run?",
        answer:
          "In the secure environment that fits your organization. We work with the infrastructure you already use and can deploy in a private cloud or on-premises when your security, compliance, or operational requirements call for it.",
      },
      {
        question: "Do we need dedicated IT staff?",
        answer:
          "No. We work alongside the people who already manage your operations and technology, whether that is an internal IT team, a managed service provider, or a designated operations lead. Continued support can cover maintenance, training, and future improvements.",
      },
      {
        question: "Will deployment disrupt our operations?",
        answer:
          "It should not. We map dependencies before deployment, test integrations in controlled stages, and phase the rollout around your operating schedule. Your team reviews each major step before the solution expands to more users or workflows.",
      },
    ],
  },
  {
    id: "capabilities",
    label: "What We Can Do",
    faqs: [
      {
        question: "What services does Contextral provide?",
        answer:
          "Our six core services are Workflow Automation for repetitive processes, Private Intelligence for secure knowledge access, Records Modernization for paper and legacy content, Systems Integration for connected tools and data, Secure AI Integration for governed AI capabilities, and Handbooks & SOPs for turning operational knowledge into clear, maintainable procedures. Outside of these, we can also do custom consulting and software development, depending on the scope of the engagement.",
      },
      {
        question: "Can it search and summarize our documents?",
        answer:
          "Yes. We can make approved PDFs, scanned records, shared drives, databases, and other internal sources searchable in plain language. Results can include citations back to source material so staff can verify answers instead of relying on an unsupported summary.",
      },
      {
        question: "Can you automate work beyond document search?",
        answer:
          "Yes. We automate document intake, classification, data entry, routing, approvals, notifications, reporting, and other repetitive steps. The goal is an end-to-end workflow that connects the tools your team already uses, not another isolated chatbot.",
      },
      {
        question: "Can you modernize paper and legacy records?",
        answer:
          "Yes. Records Modernization can include scanning, OCR, metadata extraction, classification, retention-aware organization, and migration into searchable repositories. We plan the work around your records policies and the way staff need to retrieve information.",
      },
      {
        question: "Do you build computer vision solutions?",
        answer:
          "Yes. We build computer vision for use cases such as object and event detection, intelligent footage search, safety monitoring, and operational alerts. We scope each implementation around a defined business need, authorized data sources, and appropriate human review.",
      },
      {
        question: "Does Contextral integrate with our existing tools?",
        answer:
          "Yes. Our systems integrate approved cloud platforms, databases, shared drives, document management systems, business applications, and internal APIs so information can move securely without replacing the systems that already work for you.",
      },
      {
        question: "Will automation replace our staff?",
        answer:
          "No. We design automation to remove repetitive searching, rekeying, routing, and filing so staff can focus on judgment, service, and higher-value work. People remain responsible for exceptions, approvals, and decisions that require human context.",
      },
    ],
  },
  {
    id: "by-industry",
    label: "Questions by Industry",
    faqs: [
      {
        question: "Municipal government: can you automate records requests and permits?",
        answer:
          "Yes. We can connect intake forms, records repositories, department approvals, status notifications, and reporting so requests and permits move through a consistent, traceable process while staff retain control of final decisions.",
      },
      {
        question: "Financial services: can you work with sensitive client and compliance data?",
        answer:
          "Yes. We can deploy a private automation layer around your existing CRM, portfolio, document, and legacy systems. Common workflows include client-record search, onboarding, KYC/AML checks, dynamic SOPs, systems integration, secure archiving, and structured reporting for SEC and FINRA examinations. Access, retention, and human review are defined with your organization before deployment.",
      },
      {
        question: "Distribution and wholesale: can you connect records across multiple sites?",
        answer:
          "Yes. We can unify supplier, inventory, compliance, purchasing, and operations records across locations while keeping existing business systems in place. Automated classification and routing help each site follow the same process without duplicating manual work.",
      },
      {
        question: "Healthcare and biotech: can you support HIPAA-sensitive workflows?",
        answer:
          "Yes. We design around your HIPAA safeguards, approved access model, retention requirements, and existing clinical or administrative systems. Common projects include records retrieval, intake processing, compliance documentation, and internal knowledge workflows.",
      },
      {
        question: "Education and research: can you protect confidential records?",
        answer:
          "Yes. We can automate approved workflows for student, personnel, grant, policy, and research records using role-based access and traceable processes. Solutions are tailored to the institution's privacy rules and existing technology environment.",
      },
      {
        question: "Law and legal services: can you work with privileged data?",
        answer:
          "Yes. We can build secure matter search, document intake, chronology, drafting-support, and knowledge workflows around your firm's privilege controls and ethical obligations. Source citations and human review help attorneys verify outputs before use.",
      },
    ],
  },
];

// Flattened, de-duplicated list for the page's single FAQPage JSON-LD graph.
export const allFaqs: FaqItem[] = faqGroups
  .flatMap((group) => group.faqs)
  .filter((faq, index, list) => list.findIndex((other) => other.question === faq.question) === index);
